// Copyright (c) Brock Allen & Dominick Baier. All rights reserved.
// Licensed under the Apache License, Version 2.0. See LICENSE in the project root for license information.

import { Logger, CryptoUtils } from "./utils";
import { State } from "./State";

/**
 * @public
 */
export class SigninState extends State {
    
    // to ensure state still matches settings
    /** @see {@link OidcClientSettings.authority} */
    public readonly authority: string;
    /** @see {@link OidcClientSettings.client_id} */
    public readonly app_id: string;
    /** @see {@link OidcClientSettings.redirect_uri} */
    public readonly redirect_uri: string;
   
    /** @see {@link OidcClientSettings.client_secret} */
    public readonly app_secret: string | undefined;
    /** @see {@link OidcClientSettings.extraTokenParams} */
    public readonly extraTokenParams: Record<string, unknown> | undefined;
    /** @see {@link OidcClientSettings.response_mode} */
    public readonly response_mode: "query" | "fragment" | undefined;

    public readonly skipUserInfo: boolean | undefined;
    public readonly sealed_app_id: string | undefined;

    public constructor(args: {
        id?: string;
        data?: unknown;
        created?: number;
        request_type?: string;

        authority: string;
        app_id: string;
        sealed_app_id?: string;
        redirect_uri: string;
        app_secret?: string;
        extraTokenParams?: Record<string, unknown>;
        response_mode?: "query" | "fragment";
        skipUserInfo?: boolean;
    }) {
        super(args);

        this.authority = args.authority;
        this.app_id = args.app_id;
        this.redirect_uri = args.redirect_uri;
        this.app_secret = args.app_secret;
        this.extraTokenParams = args.extraTokenParams;

        this.response_mode = args.response_mode;
        this.skipUserInfo = args.skipUserInfo;

        if (args.app_secret) {
            this.sealed_app_id = CryptoUtils.generateSealedAppId(args.app_id, args.app_secret);
        }
        else {
            this.sealed_app_id = args.sealed_app_id;
        }
    }

    public toStorageString(): string {
        new Logger("SigninState").create("toStorageString");
        return JSON.stringify({
            id: this.id,
            data: this.data,
            created: this.created,
            request_type: this.request_type,

            authority: this.authority,
            app_id: this.app_id,
            redirect_uri: this.redirect_uri,
            app_secret: this.app_secret,
            extraTokenParams : this.extraTokenParams,
            response_mode: this.response_mode,
            skipUserInfo: this.skipUserInfo,
            sealed_app_id: this.sealed_app_id
        });
    }

    public static fromStorageString(storageString: string): SigninState {
        Logger.debug("SigninState.fromStorageString");
        const data = JSON.parse(storageString);
        return new SigninState(data);
    }
}
